import { PaperClipIcon, PhotographIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph, SectionHeading, tone, useCopyGap } from './primitives';

import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepSeekSvg from '~/svg/deepseek.svg';
import GeminiSvg from '~/svg/google.svg';
import MetaSvg from '~/svg/meta.svg';
import MoonshotSvg from '~/svg/moonshot.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import XaiSvg from '~/svg/xai.svg';
import ZaiSvg from '~/svg/zai.svg';

type Logo = ComponentType<SVGProps<SVGSVGElement>>;

// Greetings for the language ticker; the first repeats to close the loop.
const GREETINGS = [
  'Hola',
  'Bonjour',
  'こんにちは',
  'Hallo',
  'Olá',
  '你好',
  'Ciao',
  'Xin chào',
  'Hola',
];

export default function AgentControl() {
  const { t } = useLocale('chatbots');
  const gap = useCopyGap();

  return (
    <section id='control' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Your agent, your rules.')}
          tail={t('Pick the model, the tone and the limits.')}
        />

        <div className='mt-16 grid gap-5 lg:grid-cols-3'>
          <Tile
            className='lg:row-span-2'
            title={t('The best AI models')}
            detail={t(
              'GPT, Claude, Gemini, DeepSeek, Grok and more. Switch whenever you like, with no retraining.',
            )}
          >
            <ModelPicker />
          </Tile>

          <Tile
            className='lg:col-span-2'
            title={t('Instructions in plain language')}
            detail={t(
              'Tell it who it is, how to sound and where the lines are. No prompt engineering needed.',
            )}
          >
            <div className='rounded-2xl bg-[#f7f7fa] p-5 dark:bg-white/[0.04]'>
              <p
                className={clsxm(
                  'text-xs font-semibold uppercase tracking-wide',
                  tone.quiet,
                )}
              >
                {t('Instructions')}
              </p>
              <p className={clsxm('mt-2.5 text-[15px] leading-7', tone.ink)}>
                {t(
                  'You are the front desk of Hotel Marisol. Keep answers short and warm.',
                )}
                {gap}
                <mark className='rounded bg-primary-100 px-1 text-primary-800 dark:bg-primary-400/20 dark:text-primary-200'>
                  {t('Never promise a refund.')}
                </mark>
                {gap}
                {t('Offer to email a manager instead.')}
                <span className='ms-0.5 inline-block h-5 w-px translate-y-1 animate-pulse bg-primary-600 motion-reduce:animate-none' />
              </p>
            </div>
          </Tile>

          <Tile
            title={t('Looks like your brand')}
            detail={t(
              'Your colors, avatar and welcome message. Hide our badge on Unlimited 200 and up.',
            )}
          >
            <BrandingPreview />
          </Tile>

          <Tile
            title={t('Understands screenshots')}
            detail={t(
              'Customers can attach photos and screenshots, and your agent reads them.',
            )}
          >
            <ScreenshotChat />
          </Tile>

          <div
            className={clsxm(
              'grid items-center gap-8 overflow-hidden rounded-[28px] p-7 sm:p-10 lg:col-span-3 lg:grid-cols-[1fr_auto]',
              tone.surface,
              tone.line,
            )}
          >
            <div>
              <h3 className={clsxm('text-lg font-semibold', tone.ink)}>
                {t('Fluent in 80+ languages')}
              </h3>
              <p
                className={clsxm(
                  'mt-2 max-w-md text-[15px] leading-7',
                  tone.body,
                )}
              >
                {t(
                  'It replies in the language your customer writes in, automatically.',
                )}
              </p>
            </div>
            <div
              aria-hidden='true'
              className={clsxm(
                styles.display,
                'h-[1.25em] overflow-hidden text-5xl font-semibold text-primary-600 sm:text-7xl dark:text-primary-400',
              )}
            >
              <div className={styles.ticker}>
                {GREETINGS.map((greeting, index) => (
                  <p key={index} className='h-[1.25em] leading-[1.25em]'>
                    {greeting}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Tile({
  title,
  detail,
  className,
  children,
}: {
  title: string;
  detail: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm(
        'flex flex-col gap-7 rounded-[28px] p-7 sm:p-8',
        tone.surface,
        tone.line,
        className,
      )}
    >
      <div>
        <h3 className={clsxm('text-lg font-semibold', tone.ink)}>{title}</h3>
        <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
          {detail}
        </p>
      </div>
      <div aria-hidden='true' className='mt-auto'>
        {children}
      </div>
    </div>
  );
}

function ModelPicker() {
  const { t } = useLocale('chatbots');
  const models: {
    name: string;
    logo: Logo;
    logoClass?: string;
    tag?: string;
  }[] = [
    {
      name: 'GPT-6 Luna',
      logo: OpenAISvg,
      logoClass: 'fill-[#10101c] dark:fill-[#10101c]',
      tag: t('Default'),
    },
    { name: 'Claude Sonnet 5', logo: AnthropicSvg },
    { name: 'Gemini 3.8 Flash', logo: GeminiSvg },
    { name: 'DeepSeek V4 Flash', logo: DeepSeekSvg },
    {
      name: 'Grok 4.6',
      logo: XaiSvg,
      logoClass: 'text-[#10101c]',
    },
    { name: 'GLM 5.3', logo: ZaiSvg },
    {
      name: 'Kimi K2.6',
      logo: MoonshotSvg,
      logoClass: 'text-[#10101c]',
    },
    { name: 'Muse Spark 1.3', logo: MetaSvg },
  ];

  return (
    <div className='rounded-2xl bg-[#f7f7fa] p-2 dark:bg-white/[0.04]'>
      <ul className='space-y-1'>
        {models.map(({ name, logo: ModelLogo, logoClass, tag }, index) => (
          <li
            key={name}
            className={clsxm(
              'flex items-center gap-3 rounded-xl px-3 py-2.5',
              index === 0 && clsxm(tone.surface, tone.line),
            )}
          >
            <span className='flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white ring-1 ring-black/[0.06] dark:bg-white/90'>
              <ModelLogo className={clsxm('h-4 w-4', logoClass)} />
            </span>
            <span
              className={clsxm('flex-1 truncate text-sm font-medium', tone.ink)}
            >
              {name}
            </span>
            {tag && (
              <span className='rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-400/15 dark:text-primary-300'>
                {tag}
              </span>
            )}
            {index === 0 && (
              <CheckGlyph className='text-primary-600 dark:text-primary-400' />
            )}
          </li>
        ))}
      </ul>
      <p className={clsxm('px-3 pb-2 pt-3 text-xs', tone.quiet)}>
        {t('And dozens more')}
      </p>
    </div>
  );
}

function BrandingPreview() {
  const { t } = useLocale('chatbots');
  const swatches = [
    'bg-[#b64d27]',
    'bg-primary-600',
    'bg-[#0f6b66]',
    'bg-[#1d4ed8]',
    'bg-[#10101c] dark:bg-white',
  ];

  return (
    <div className='rounded-2xl bg-[#f7f7fa] p-5 dark:bg-white/[0.04]'>
      <div className='flex items-center gap-2.5'>
        {swatches.map((swatch, index) => (
          <span
            key={swatch}
            className={clsxm(
              'h-7 w-7 rounded-full',
              swatch,
              index === 0 &&
                'ring-2 ring-[#b64d27] ring-offset-2 ring-offset-[#f7f7fa] dark:ring-offset-[#1b1b27]',
            )}
          />
        ))}
      </div>
      <div className='mt-5 flex items-end justify-between gap-4'>
        <p
          className={clsxm(
            'max-w-[11rem] rounded-2xl rounded-ee-md px-3.5 py-2.5 text-[13px] leading-snug shadow-sm',
            tone.surface,
            tone.line,
            tone.ink,
          )}
        >
          {t('Hi! Need a room, or help with a booking?')}
        </p>
        <span className='flex h-12 w-12 flex-none items-center justify-center rounded-full bg-[#b64d27] text-white shadow-lg shadow-[#b64d27]/30'>
          <svg viewBox='0 0 24 24' fill='currentColor' className='h-6 w-6'>
            <path d='M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z' />
            <path d='M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z' />
          </svg>
        </span>
      </div>
      <div className='mt-5 flex items-center justify-between border-t border-[#0f0f28]/[0.07] pt-4 dark:border-white/[0.07]'>
        <span className={clsxm('text-[13px] font-medium', tone.ink)}>
          {t('Show “Powered by” badge')}
        </span>
        <span className='relative inline-flex h-5 w-9 items-center rounded-full bg-[#dcdce6] dark:bg-white/15'>
          <span className='absolute start-0.5 h-4 w-4 rounded-full bg-white shadow' />
        </span>
      </div>
    </div>
  );
}

function ScreenshotChat() {
  const { t } = useLocale('chatbots');

  return (
    <div className='space-y-3 rounded-2xl bg-[#f7f7fa] p-5 dark:bg-white/[0.04]'>
      <div className='flex justify-end'>
        <div
          className={clsxm(
            'w-[13.5rem] overflow-hidden rounded-2xl rounded-ee-md shadow-sm',
            tone.surface,
            tone.line,
          )}
        >
          {/* A tiny drawn "screenshot" of a failed payment. */}
          <div className='space-y-2 bg-[#fdf2f2] p-3 dark:bg-[#2a1618]'>
            <div className='flex items-center gap-1.5'>
              <span className='h-2 w-2 rounded-full bg-[#e5484d]' />
              <span className='h-1.5 w-20 rounded-full bg-[#e5484d]/40' />
            </div>
            <span className='block h-1.5 w-32 rounded-full bg-[#0f0f28]/15 dark:bg-white/15' />
            <span className='block h-1.5 w-24 rounded-full bg-[#0f0f28]/15 dark:bg-white/15' />
          </div>
          <p
            className={clsxm(
              'flex items-center gap-1.5 px-3 py-2 text-xs',
              tone.body,
            )}
          >
            <PhotographIcon className='h-3.5 w-3.5' />
            {t('Why did my payment fail?')}
          </p>
        </div>
      </div>
      <p className='max-w-[88%] rounded-2xl rounded-es-md bg-[#b64d27] px-3.5 py-2.5 text-[13px] leading-snug text-white'>
        {t(
          'Your card was declined because it expired in May. You can add a new one under Billing.',
        )}
      </p>
      <p className={clsxm('flex items-center gap-1.5 text-xs', tone.quiet)}>
        <PaperClipIcon className='h-3.5 w-3.5' />
        {t('PNG, JPEG or WebP, up to 4 per message')}
      </p>
    </div>
  );
}
